import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { getBoxTypes } from '../../../redux/actions'
import { requestCompartment } from '../../../redux/parcels/slice.parcels'
import { showProblem, showDashedResponseText } from '../showResponseText'

const nameOf = (boxType) => boxType?.display?.friendlyName?.default

// Each size once, in the order the location lists them, and the sizes that
// still have a free, active compartment.
const sizesOf = (boxes) => {
  const sameSize = (a, b) => nameOf(a) === nameOf(b)
  const available = []
  const sizes = []
  boxes.forEach((box) => {
    const isActive =
      !Object.prototype.hasOwnProperty.call(box, 'active') ||
      box.active === true
    const isFree = !box.parcelId && !box.bookingId
    if (
      isActive &&
      isFree &&
      !available.some((s) => sameSize(s, box.boxTypeDetails))
    ) {
      available.push(box.boxTypeDetails)
    }
    if (!sizes.some((s) => sameSize(s, box.boxTypeDetails))) {
      sizes.push(box.boxTypeDetails)
    }
  })
  return {
    sizes: sizes.sort((a, b) => a.display.order - b.display.order),
    available,
  }
}

// The registry covers the user's `requestLargerCompartment` and the parcel's
// `canChangeBoxSize`. What it cannot say is that the location has box types at
// all, which is fetched data, so with none this returns null.
export default function ChangeBoxSize({ parcel, onChanged }) {
  const { t } = useTranslation('parcels')
  const { t: transResponseMessage } = useTranslation('responseMessages')
  const dispatch = useDispatch()
  const [boxTypes, setBoxTypes] = useState([])
  const [availableBoxTypes, setAvailableBoxTypes] = useState([])
  const [saving, setSaving] = useState(false)
  const locationId = parcel.location?._id

  useEffect(() => {
    // another location's sizes are not this one's, whatever the new fetch does
    setBoxTypes((previous) => (previous.length ? [] : previous))
    setAvailableBoxTypes((previous) => (previous.length ? [] : previous))
    if (!locationId) return undefined
    let cancelled = false
    // a failed read comes back as {error} or nothing at all, or the request
    // throws; the section then stays away
    dispatch(getBoxTypes(locationId))
      .then((data) => {
        if (cancelled || !data || data.error) return
        const boxes = data.response?.boxTypes
        if (!Array.isArray(boxes)) return
        const { sizes, available } = sizesOf(boxes)
        setBoxTypes(sizes)
        setAvailableBoxTypes(available)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [dispatch, locationId])

  if (boxTypes.length === 0) return null

  const currentName = nameOf(parcel.boxType)

  const submitBookingRequest = async (sizeName) => {
    setSaving(true)
    try {
      const { payload } = await dispatch(
        requestCompartment({ parcelId: parcel.parcelId, sizeName })
      )
      // the server's messages are keyed with dashes where it wrote spaces
      showDashedResponseText(payload, transResponseMessage)
    } catch (e) {
      // requestCompartment has already shown its own error when the request
      // failed; this is for anything thrown around it
      showProblem(transResponseMessage)
    } finally {
      setSaving(false)
      onChanged()
    }
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6" align="center">
        {t('changeBoxSize')}
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        useFlexGap
        flexWrap="wrap"
      >
        {boxTypes.map((boxType) => {
          const { dimensions } = boxType
          if (!dimensions) return null
          const name = nameOf(boxType)
          const available = availableBoxTypes.some((s) => nameOf(s) === name)
          return (
            <Stack key={name} spacing={1} alignItems="center">
              <Box
                sx={{
                  border: 1,
                  borderColor: 'grey.500',
                  borderRadius: 1,
                  p: 2,
                  bgcolor: 'background.paper',
                  opacity: available ? 1 : 0.5,
                }}
              >
                <Typography variant="body2">
                  {t('height')}: {dimensions.height}
                </Typography>
                <Typography variant="body2">
                  {t('depth')}: {dimensions.length}
                </Typography>
                <Typography variant="body2">
                  {t('width')}: {dimensions.width}
                </Typography>
              </Box>
              {(currentName ?? '').toLowerCase() !== name.toLowerCase() ? (
                <Button
                  variant="contained"
                  color="iboxen"
                  size="small"
                  disabled={!available || saving}
                  onClick={() => submitBookingRequest(name)}
                >
                  {t('book')} {t(`boxTypes.${name}`)}
                </Button>
              ) : (
                <Typography variant="body2">{t('currentBox')}</Typography>
              )}
            </Stack>
          )
        })}
      </Stack>
    </Stack>
  )
}
